// Cancelar una compra con motivo (Bramarket punto 3, 10-oct-2026). Política A (Carlos): se le quita al cliente el
// cashback/puntos que le dio esa compra aunque su saldo quede NEGATIVO; lo siguiente que gane lo cubre primero.
// La compra no se borra: queda «Cancelada por X: motivo» en el historial y en el Excel, y su folio queda libre.
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Ban, Loader2, X } from 'lucide-react';
import { clientsApi } from '../../../api/clientsApi';

export interface CancelableVisit {
  id: string;
  clientName?: string | null;
  dateLabel: string;
  amount?: number | null;
  ticketRef?: string;
  earnedPoints: number;
}

interface Props {
  visit: CancelableVisit;
  isCashback: boolean;
  onClose: () => void;
  onCancelled: (r: { balance: number; negative: boolean; removed: number }) => void;
}

const MOTIVOS = ['Devolución de la compra', 'Error de captura (monto)', 'Cliente equivocado', 'Ticket cancelado en caja'];

const CancelVisitModal: React.FC<Props> = ({ visit, isCashback, onClose, onCancelled }) => {
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fmt = (n: number) => (isCashback ? `$${n.toFixed(2)}` : `${Math.floor(n)} pts`);
  const valido = motivo.trim().length >= 3;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valido || busy) return;
    setBusy(true); setError('');
    try {
      const r = await clientsApi.cancelVisit(visit.id, motivo.trim());
      onCancelled(r);
    } catch (err: any) {
      setError(err?.message || 'No se pudo cancelar la compra');
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={enviar} data-testid="cancelar-compra"
        className="modal-sheet w-full rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-content">
            <Ban size={18} className="text-red-500" /> Cancelar compra
          </h2>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>
        <p className="mb-4 text-sm text-content-2">
          {visit.clientName || 'Cliente'} · {visit.dateLabel}
          {visit.amount != null && visit.amount > 0 ? ` · Venta $${visit.amount.toFixed(2)}` : ''}
          {visit.ticketRef ? ` · Folio ${visit.ticketRef}` : ''}
        </p>

        {visit.earnedPoints > 0 ? (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>
              Se le quitarán <b>{fmt(visit.earnedPoints)}</b> que le dio esta compra. Si ya los gastó, su saldo quedará
              en negativo y lo próximo que gane lo cubre primero.
            </span>
          </div>
        ) : (
          <p className="mb-4 text-sm text-content-3">Esta compra no le dio {isCashback ? 'cashback' : 'puntos'}: solo quedará marcada como cancelada.</p>
        )}

        <label className="mb-1 block text-xs font-medium text-content-2">Motivo (obligatorio)</label>
        <div className="mb-2 flex flex-wrap gap-1.5">
          {MOTIVOS.map((m) => (
            <button key={m} type="button" onClick={() => setMotivo(m)}
              className={`rounded-full border px-3 py-1 text-xs ${motivo === m ? 'border-accent bg-accent/10 text-accent' : 'border-line text-content-2 hover:bg-surface-2'}`}>
              {m}
            </button>
          ))}
        </div>
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={200} data-testid="cancelar-motivo"
          placeholder="Escribe el motivo"
          className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-content outline-none focus:border-accent/50" />
        <p className="mt-2 text-[11px] text-content-3">
          La compra no se borra: queda «Cancelada» con tu nombre y el motivo en el historial y en el Excel. Su folio se puede volver a registrar.
        </p>

        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400" data-testid="cancelar-error">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm text-content-2 hover:bg-surface-2">Volver</button>
          <button type="submit" disabled={!valido || busy}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Ban size={16} />} Cancelar compra
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

export default CancelVisitModal;
